// src/components/templates/FlipbookTemplate.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Volume2, VolumeX, Settings, Share2, MessageSquarePlus, Menu, BookMarked, Minimize2,
  Loader, AlertCircle, Trash2, Mic, ChevronLeft, ChevronRight, Maximize2, ZoomOut, Award,
  ZoomIn, X
} from 'lucide-react';

import HTMLFlipBook from 'react-pageflip';
import { collection, doc, getDocs, updateDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { SharedHeader } from '../shared/SharedHeader';
import { ShareModal } from '../books/modals/ShareModal';
import { VoiceRecorder } from '../shared/VoiceRecorder';
import { VoicePlayer } from '../shared/VoicePlayer';
import { PageResponseModal } from '../flipbooks/PageResponseModal';
import { GradingSidebar } from '../flipbooks/GradingSidebar';

const FLIP_SOUND_BASE64 = 'data:audio/mpeg;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhgCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA//////////////////////////////////////////////////////////////////8AAAA5TEFNRTMuMTAwAZYAAAAAAAAAABQ4JAMGQgAAOAAABYY7xtBpAAAAAAD/+5DEAAAAR4EUB3xoABxJgC/ngIAPywkZFEokIoDBRGBZAwUGDhABKyEYBgQKIHAgQcBAB8IEIIFAgI/g...';

const PageWithControls = React.forwardRef(({
  pageData,
  pageIndex,
  onSetAudio,
  onPlayVoice,
  onZoom,
  currentlyPlayingPageIndex,
  isEditMode = false
}, ref) => {
  const [showRecorder, setShowRecorder] = useState(false);
  const isRightPage = pageIndex % 2 === 0;
  const hasVoice = pageData.voiceUrl;
  const isPlaying = currentlyPlayingPageIndex === pageIndex;

  const handleSaveVoice = async (voiceData) => {
    await onSetAudio(voiceData, pageIndex);
    setShowRecorder(false);
  };

  return (
    <>
      <div className="bg-white shadow-inner flex items-center justify-center relative group w-full h-full" ref={ref}>
        <img
          src={pageData.imageUrl}
          alt={`Page ${pageIndex + 1}`}
          className="w-full h-full object-contain"
          loading="lazy"
        />


        {isEditMode && (
          <div className={`absolute top-2 ${isRightPage ? 'left-2' : 'left-12'} z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
            <div className="flex items-center gap-2 p-1 bg-gray-900/80 backdrop-blur-sm rounded-lg shadow-lg">
              {!hasVoice ? (
                <button
                  onClick={() => setShowRecorder(true)}
                  className="p-2 text-white rounded-full hover:bg-purple-500 transition-colors"
                  title="Add narration"
                >
                  <Mic className="w-5 h-5" />
                </button>
              ) : (
                <>
                  <button
                    onClick={() => onPlayVoice(pageIndex)}
                    className="p-2 text-white rounded-full hover:bg-green-500 transition-colors"
                    title={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => onSetAudio(null, pageIndex)}
                    className="p-2 text-white rounded-full hover:bg-red-500 transition-colors"
                    title="Delete narration"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {!isEditMode && hasVoice && (
          <button
            onClick={() => onPlayVoice(pageIndex)}
            className={`absolute top-4 left-4 p-3 rounded-full shadow-lg transition-all ${isPlaying
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white scale-110'
                : 'bg-white/90 backdrop-blur-sm text-purple-600 hover:scale-105'
              }`}
            title="Play narration"
          >
            {isPlaying ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
          </button>
        )}
      </div>

      {showRecorder && (
        <VoiceRecorder
          context="page"
          contextData={{ pageIndex, pageId: pageData.id }}
          showSpeakerInfo={false}
          maxDuration={180}
          onSave={handleSaveVoice}
          onCancel={() => setShowRecorder(false)}
        />
      )}
    </>
  );
});

PageWithControls.displayName = 'PageWithControls';

export const FlipbookTemplate = ({ bookId, bookData, user, customStyling = {} }) => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [booklets, setBooklets] = useState([]);
  const [currentBookletIndex, setCurrentBookletIndex] = useState(0);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [currentlyPlayingPageIndex, setCurrentlyPlayingPageIndex] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [error, setError] = useState(null);
  const [currentVoice, setCurrentVoice] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isEditingPageNumber, setIsEditingPageNumber] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [showGradingSidebar, setShowGradingSidebar] = useState(false);

  const flipBookRef = useRef(null);
  const flipAudioRef = useRef(null);
  const containerRef = useRef(null);

  const styling = {
    primaryColor: customStyling.primaryColor || bookData?.styling?.primaryColor || '#8b5cf6',
    backgroundColor: customStyling.backgroundColor || bookData?.styling?.backgroundColor || '#f8fafc',
    headerColor: customStyling.headerColor || bookData?.styling?.headerColor || '#ffffff',
    fontFamily: customStyling.fontFamily || bookData?.styling?.fontFamily || 'Inter, sans-serif'
  };

  const isSinglePageMode = bookData?.flipbookSettings?.viewMode === 'single';
  const flipbookSettings = bookData?.flipbookSettings || {};
  const isEditor = flipbookSettings?.editors?.includes(authUser?.uid);
  const headerSettings = bookData?.headerSettings || {};
  const showHeader = headerSettings.showHeader !== false;

  useEffect(() => {
    if (showGradingSidebar) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showGradingSidebar]);

  useEffect(() => {
    loadBooklets();
  }, [bookId]);

  useEffect(() => {
    if (authUser && bookData) {
      setIsOwner(authUser.uid === bookData.ownerId);
    }
  }, [authUser, bookData]);

  useEffect(() => {
    if (booklets.length > 0 && !isTransitioning) {
      loadPages(booklets[currentBookletIndex].id);
    }
  }, [booklets, currentBookletIndex, isTransitioning]);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    const timer = setTimeout(updateSize, 100);

    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timer);
    };
  }, [booklets.length]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Replace calculateBookSize function
  const calculateBookSize = () => {
    if (!containerSize.width || !containerSize.height) return { width: 400, height: 600 };

    const minWidth = flipbookSettings.minWidth || 300;
    const maxWidth = flipbookSettings.maxWidth || 1000;
    const minHeight = flipbookSettings.minHeight || 400;
    const maxHeight = flipbookSettings.maxHeight || 1500;

    const isMobile = window.innerWidth < 768;
    const pageAspectRatio = 0.8;

    const availableWidth = isMobile
      ? Math.min(containerSize.width * 0.95, window.innerWidth - 20)
      : containerSize.width * 0.9;

    const availableHeight = isMobile
      ? Math.min(containerSize.height * 0.85, window.innerHeight - 180)
      : containerSize.height * 0.92;

    let height = availableHeight;
    let width = height * pageAspectRatio;

    const maxAllowedWidth = isSinglePageMode
      ? Math.min(maxWidth, availableWidth)
      : Math.min(maxWidth * 2, availableWidth);

    if (width > maxAllowedWidth) {
      width = maxAllowedWidth;
      height = width / pageAspectRatio;
    }

    if (isMobile) {
      width = Math.max(minWidth * 0.9, Math.min(width, window.innerWidth - 20));
      height = Math.max(minHeight * 0.9, Math.min(height, window.innerHeight - 180));
    } else {
      width = Math.max(minWidth, Math.min(width, maxWidth));
      height = Math.max(minHeight, Math.min(height, maxHeight));
    }

    return { width, height };
  };

  // Add toggleFullscreen function
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch(err => {
        console.error('Error entering fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const { width, height } = calculateBookSize();

  const loadBooklets = async () => {
    if (!bookId) return;

    try {
      setLoading(true);
      setError(null);

      const bookletsRef = collection(db, 'pb-books', bookId, 'pb-booklets');
      const q = query(bookletsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      const bookletsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setBooklets(bookletsData);
    } catch (err) {
      console.error('Error loading booklets:', err);
      setError('Failed to load flipbook booklets');
    } finally {
      setLoading(false);
    }
  };

  const loadPages = async (bookletId) => {
    try {
      const pagesRef = collection(db, 'pb-books', bookId, 'pb-booklets', bookletId, 'pages');
      const q = query(pagesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      const pagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPages(pagesData);
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  const changeBooklet = async (direction) => {
    let newIndex;

    if (typeof direction === 'number') {
      newIndex = direction;
    } else {
      newIndex = direction === 'next'
        ? Math.min(currentBookletIndex + 1, booklets.length - 1)
        : Math.max(currentBookletIndex - 1, 0);
    }

    if (newIndex !== currentBookletIndex) {
      setCurrentlyPlayingPageIndex(null);
      setCurrentVoice(null);
      setIsTransitioning(true);
      setPages([]);

      if (flipBookRef.current) {
        flipBookRef.current = null;
      }

      await new Promise(resolve => setTimeout(resolve, 150));
      setCurrentBookletIndex(newIndex);
      await new Promise(resolve => setTimeout(resolve, 100));
      setIsTransitioning(false);
    }
  };

  const handleSetPageAudio = async (voiceData, pageIndex) => {
    try {
      const pageId = pages[pageIndex].id;
      const bookletId = booklets[currentBookletIndex].id;

      if (voiceData) {
        const audioRef = ref(storage, `pb-books/${bookId}/booklets/${bookletId}/pages/${pageId}/voice.webm`);
        await uploadBytes(audioRef, voiceData.blob);
        const audioUrl = await getDownloadURL(audioRef);

        const pageRef = doc(db, 'pb-books', bookId, 'pb-booklets', bookletId, 'pages', pageId);
        await updateDoc(pageRef, { voiceUrl: audioUrl });

        setPages(prev => prev.map((p, i) =>
          i === pageIndex ? { ...p, voiceUrl: audioUrl } : p
        ));
      } else {
        const pageRef = doc(db, 'pb-books', bookId, 'pb-booklets', bookletId, 'pages', pageId);
        await updateDoc(pageRef, { voiceUrl: null });

        setPages(prev => prev.map((p, i) =>
          i === pageIndex ? { ...p, voiceUrl: null } : p
        ));
      }
    } catch (err) {
      console.error('Error setting page audio:', err);
    }
  };

  const handlePlayVoice = (pageIndex) => {
    const page = pages[pageIndex];
    if (!page.voiceUrl) return;

    if (currentlyPlayingPageIndex === pageIndex) {
      setCurrentlyPlayingPageIndex(null);
      setCurrentVoice(null);
    } else {
      setCurrentlyPlayingPageIndex(pageIndex);
      setCurrentVoice({
        url: page.voiceUrl,
        title: `Page ${pageIndex + 1}`
      });
    }
  };

  const onFlip = useCallback((e) => {
    if (isSoundOn && flipAudioRef.current) {
      flipAudioRef.current.currentTime = 0;
      flipAudioRef.current.play().catch(err => console.error("Flip audio play failed:", err));
    }
    setCurrentPage(e.data);
    setCurrentlyPlayingPageIndex(null);
    setCurrentVoice(null);
  }, [isSoundOn]);

  const handlePrevPage = () => {
    if (!flipBookRef.current?.pageFlip()) return;

    const pageFlip = flipBookRef.current.pageFlip();
    const currentPageNum = pageFlip.getCurrentPageIndex();

    if (isSinglePageMode) {
      // In single page mode with cover, ensure we don't go before page 0
      if (currentPageNum > 0) {
        pageFlip.flipPrev();
      }
    } else {
      // In double page mode, flip two pages at once (left and right)
      if (currentPageNum >= 2) {
        pageFlip.flipPrev();
      }
    }
  };

  const goToPage = (pageIndex) => {
    if (flipBookRef.current?.pageFlip) {
      flipBookRef.current.pageFlip().turnToPage(pageIndex);
    }
  };

  const handleNextPage = () => {
    if (!flipBookRef.current?.pageFlip()) return;

    const pageFlip = flipBookRef.current.pageFlip();
    const currentPageNum = pageFlip.getCurrentPageIndex();
    const totalPages = pages.length;

    if (isSinglePageMode) {
      // In single page mode, flip one page at a time
      if (currentPageNum < totalPages - 1) {
        pageFlip.flipNext();
      }
    } else {
      // In double page mode, flip two pages at once
      if (currentPageNum < totalPages - 2) {
        pageFlip.flipNext();
      }
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: styling.backgroundColor }}
      >
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: styling.primaryColor }} />
          <p className="text-gray-600">Loading flipbook...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: styling.backgroundColor }}
      >
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadBooklets}
            className="px-6 py-2 rounded-lg text-white transition-colors"
            style={{ backgroundColor: styling.primaryColor }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (booklets.length === 0) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: styling.backgroundColor }}
      >
        <div className="text-center">
          <BookOpen className="w-20 h-20 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">No booklets available</p>
          {isOwner && (
            <>
              <p className="text-gray-500 text-sm mt-2">Create a booklet to get started</p>
              <button
                onClick={() => navigate(`/books/${bookId}/edit`)}
                className="mt-4 px-6 py-2 rounded-lg text-white"
                style={{ backgroundColor: styling.primaryColor }}
              >
                Manage Flipbook
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const currentBooklet = booklets[currentBookletIndex];

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{
        backgroundColor: styling.backgroundColor,
        fontFamily: styling.fontFamily
      }}
    >
      {/* Header Section */}
      {showHeader && (
        <SharedHeader
          templateType="flipbook"
          bookData={bookData}
          styling={styling}
          isOwner={isOwner}
          navigate={navigate}
          stats={{
            views: bookData?.analytics?.views || 0,
            booklets: booklets.length,
            pages: pages.length
          }}
        />
      )}
      {/* SIDEBAR MENU */}
      {booklets.length > 1 && (
        <>
          {/* Sidebar Backdrop */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`fixed left-0 top-20 h-full bg-gradient-to-b from-gray-50 to-white border-r border-gray-200 shadow-2xl z-50 transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            style={{ width: '280px' }}
          >
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BookMarked className="w-5 h-5" style={{ color: styling.primaryColor }} />
                  <h3 className="font-bold text-gray-900">Booklets</h3>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <p className="text-sm text-gray-500">{booklets.length} total booklets</p>
            </div>

            {/* Booklets List */}
            <div className="overflow-y-auto h-[calc(100%-180px)] p-3">
              {booklets.map((booklet, index) => (
                <button
                  key={booklet.id}
                  onClick={() => {
                    if (index !== currentBookletIndex) {
                      changeBooklet(index > currentBookletIndex ? 'next' : 'prev');
                    }
                    if (window.innerWidth < 1024) {
                      setSidebarOpen(false);
                    }
                  }}
                  className={`w-full text-left p-4 rounded-xl mb-2 transition-all duration-200 ${index === currentBookletIndex
                      ? 'text-gray-900 bg-gradient-to-r from-purple-200 to-pink-100 shadow-xl scale-[1.02]'
                      : 'bg-white hover:bg-gray-50 border border-gray-200 hover:border-purple-300'
                    }`}
                  style={{
                    boxShadow: `
                      inset 0 2px 3px rgba(0, 0, 0, 0.4),
                      0 0 8px rgba(255, 255, 255, 0.4)
                    `,
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-lg shadow-md flex items-center justify-center font-bold ${index === currentBookletIndex
                          ? 'bg-white/20 text-gray-900'
                          : 'bg-white text-gray-600'
                        }`}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold text-sm mb-1 line-clamp-2 ${index === currentBookletIndex ? 'text-gray-800' : 'text-gray-900'
                        }`}>
                        {booklet.title}
                      </h4>
                      {booklet.pageCount && (
                        <p className={`text-xs mt-1 ${index === currentBookletIndex ? 'text-gray-400' : 'text-gray-400'
                          }`}>
                          {booklet.pageCount} pages
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Sidebar Toggle Button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="fixed left-0 top-20 z-40 p-3 bg-white/90 backdrop-blur-sm rounded-r-lg shadow-lg hover:shadow-xl transition-all"
            style={{ color: styling.primaryColor }}
            title={sidebarOpen ? 'Hide booklets menu' : 'Show booklets menu'}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Flipbook Container */}
      <div
        ref={containerRef}
        className={`flex-1 flex items-center justify-center relative transition-all duration-300 ${sidebarOpen && booklets.length > 1 ? 'lg:pl-[280px]' : ''
          } ${isFullscreen ? 'bg-black' : ''}`}
        style={{
          overflow: 'auto',
          maxWidth: '100vw',
          marginBottom: '40px',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {isTransitioning || pages.length === 0 ? (
          <div className="text-center">
            <Loader className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: styling.primaryColor }} />
            <p className="text-gray-600">Loading booklet...</p>
          </div>
        ) : width > 0 && (
          <div className="relative group w-full h-full flex items-center justify-center p-2 md:p-4">
            <div
              className="relative transition-transform duration-300"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: `${width}px`,
                height: `${height}px`,
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center'
              }}
            >
              <HTMLFlipBook
                key={`flipbook-${currentBookletIndex}-${booklets[currentBookletIndex]?.id}-${isSinglePageMode}`}
                width={width}
                height={height}
                size="stretch"
                minWidth={Math.min(flipbookSettings.minWidth || 300, window.innerWidth - 40)}
                maxWidth={Math.min(flipbookSettings.maxWidth || 1000, window.innerWidth - 40)}
                minHeight={Math.min(flipbookSettings.minHeight || 400, window.innerHeight - 200)}
                maxHeight={Math.min(flipbookSettings.maxHeight || 1500, window.innerHeight - 100)}
                maxShadowOpacity={flipbookSettings.shadowOpacity || 0.5}
                showCover={isSinglePageMode}
                mobileScrollSupport={true}
                onFlip={onFlip}
                className="shadow-2xl"
                ref={flipBookRef}
                startPage={0}
                drawShadow={true}
                flippingTime={flipbookSettings.flipDuration || 1000}
                usePortrait={isSinglePageMode}
                startZIndex={0}
                autoSize={true}
                clickEventForward={false}
                useMouseEvents={true}
                swipeDistance={30}
                showPageCorners={true}
                disableFlipByClick={false}
              >
                {pages.map((page, index) => (
                  <PageWithControls
                    key={`${currentBookletIndex}-page-${page.id}-${index}`}
                    pageData={page}
                    pageIndex={index}
                    onSetAudio={handleSetPageAudio}
                    onPlayVoice={handlePlayVoice}
                    onZoom={setZoomedImageUrl}
                    currentlyPlayingPageIndex={currentlyPlayingPageIndex}
                    isEditMode={isOwner}
                  />
                ))}
              </HTMLFlipBook>

              {isSinglePageMode && (
                <>
                  <div
                    onClick={handlePrevPage}
                    className="absolute left-0 top-20 bottom-10 w-1/3 z-10 cursor-pointer hover:bg-black/5 transition-colors"
                    style={{ pointerEvents: currentPage > 0 ? 'auto' : 'none' }}
                    aria-label="Previous page"
                  />

                  <div
                    onClick={handleNextPage}
                    className="absolute right-0 top-20 bottom-10 w-1/3 z-10 cursor-pointer hover:bg-black/5 transition-colors"
                    style={{ pointerEvents: currentPage < pages.length - 1 ? 'auto' : 'none' }}
                    aria-label="Next page"
                  />
                </>
              )}

              {/* Navigation arrows */}
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                className={`absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 rounded-full bg-white/90 shadow-lg transition-all ${currentPage === 0
                    ? 'opacity-30 cursor-not-allowed'
                    : 'opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-110'
                  }`}
                style={{ color: styling.primaryColor }}
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              <button
                onClick={handleNextPage}
                disabled={currentPage >= pages.length - (isSinglePageMode ? 1 : 2)}
                className={`absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 rounded-full bg-white/90 shadow-lg transition-all ${currentPage >= pages.length - (isSinglePageMode ? 1 : 2)
                    ? 'opacity-30 cursor-not-allowed'
                    : 'opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-110'
                  }`}
                style={{ color: styling.primaryColor }}
              >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className="absolute top-2 md:top-4 right-2 md:right-4 z-20 p-2 md:p-3 rounded-full bg-white/90 shadow-lg transition-all opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-110"
                style={{ color: styling.primaryColor }}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="w-5 h-5 md:w-6 md:h-6" /> : <Maximize2 className="w-5 h-5 md:w-6 md:h-6" />}
              </button>
            </div>
          </div>
        )}
        <audio ref={flipAudioRef} src={FLIP_SOUND_BASE64} preload="auto" />

        {/* Zoom Controls - Visible in both normal and fullscreen modes */}
        <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white/20 backdrop-blur-sm rounded-full shadow-xl px-6 py-3 flex items-center gap-4 transition-all ${!isFullscreen && sidebarOpen && booklets.length > 1 ? 'lg:left-[calc(50%+140px)]' : ''
          }`}>
          <ZoomOut className="w-5 h-5 text-gray-600" />
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={zoomLevel}
            onChange={(e) => setZoomLevel(parseFloat(e.target.value))}
            className="w-32 md:w-48 h-2 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${styling.primaryColor} 0%, ${styling.primaryColor} ${(zoomLevel - 0.5) / 1.5 * 100}%, #e5e7eb ${(zoomLevel - 0.5) / 1.5 * 100}%, #e5e7eb 100%)`
            }}
          />
          <ZoomIn className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700 ml-2">{Math.round(zoomLevel * 100)}%</span>
          <button
            onClick={() => setZoomLevel(1)}
            className="ml-2 px-3 py-1 text-xs rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            style={{ color: styling.primaryColor }}
          >
            Reset
          </button>
        </div>

        {/* Page Number Display with Navigation */}
        <div className="fixed bottom-6 left-6 z-50">
          {!isEditingPageNumber ? (
            <button
              onClick={() => setIsEditingPageNumber(true)}
              className="p-3 bg-gray-800/80 backdrop-blur-sm text-white rounded-full shadow-lg font-mono text-sm select-none hover:bg-gray-700/90 transition-all cursor-pointer"
              title="Click to jump to page"
            >
              {currentPage + 1} / {pages.length}
            </button>
          ) : (
            <div className="flex items-center gap-2 p-2 bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg">
              <input
                type="number"
                min="1"
                max={pages.length}
                defaultValue={currentPage + 1}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const pageNum = parseInt(e.target.value);
                    if (pageNum >= 1 && pageNum <= pages.length) {
                      goToPage(pageNum - 1);
                      setIsEditingPageNumber(false);
                    }
                  } else if (e.key === 'Escape') {
                    setIsEditingPageNumber(false);
                  }
                }}
                onBlur={(e) => {
                  const pageNum = parseInt(e.target.value);
                  if (pageNum >= 1 && pageNum <= pages.length) {
                    goToPage(pageNum - 1);
                  }
                  setIsEditingPageNumber(false);
                }}
                className="w-16 px-2 py-1 bg-white/10 text-white text-center font-mono text-sm rounded-lg border border-white/20 focus:outline-none focus:border-white/40"
              />
              <span className="text-white text-sm font-mono">/ {pages.length}</span>
              <button
                onClick={() => setIsEditingPageNumber(false)}
                className="p-1 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* Control Buttons */}
        <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
          {(isOwner || isEditor) && flipbookSettings.enableGradingSidebar && (
            <button
              onClick={() => setShowGradingSidebar(!showGradingSidebar)}
              className={`p-3 rounded-full shadow-lg hover:shadow-2xl hover:scale-110 transition-all ${showGradingSidebar
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                  : 'bg-white'
                }`}
              style={{ color: !showGradingSidebar ? styling.primaryColor : undefined }}
              title="Grading sidebar"
            >
              <Award className="h-6 w-6" />
            </button>
          )}

          {flipbookSettings.enableResponseModal !== false && (
            <button
              onClick={() => setShowResponseModal(true)}
              className="p-3 bg-white/90 hover:bg-white/100 backdrop-blur-sm text-gray-800 rounded-full shadow-lg hover:shadow-2xl hover:scale-110 transition-all"
              title="Submit a response"
              style={{ color: styling.primaryColor }}
            >
              <MessageSquarePlus className="h-6 w-6" />
            </button>
          )}

          {isOwner && (
            <button
              onClick={() => navigate(`/books/${bookId}/edit`)}
              className="p-3 rounded-full shadow-lg hover:shadow-xl transition-all text-white"
              style={{ backgroundColor: styling.primaryColor }}
              title="Edit flipbook"
            >
              <Settings className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {showGradingSidebar && (
        <GradingSidebar
          bookId={bookId}
          bookletId={booklets[currentBookletIndex].id}
          bookletTitle={currentBooklet?.title || 'Untitled Booklet'}
          pageNumber={currentPage}
          onClose={() => setShowGradingSidebar(false)}
        />
      )}

      {currentVoice && (
        <VoicePlayer
          voice={currentVoice}
          title={`Page ${currentlyPlayingPageIndex + 1}`}
          position="bottom-left"
          autoPlay={true}
          onClose={() => {
            setCurrentlyPlayingPageIndex(null);
            setCurrentVoice(null);
          }}
        />
      )}

      {/* Response Modal */}
      {showResponseModal && (
        <PageResponseModal
          bookId={bookId}
          pageNumber={currentPage}
          bookletId={booklets[currentBookletIndex]?.id}
          bookletTitle={booklets[currentBookletIndex]?.title}
          bookOwnerId={bookData?.ownerId}
          bookData={bookData}
          user={user}
          onClose={() => setShowResponseModal(false)}
          onSubmitSuccess={() => {
            alert('Response submitted successfully!');
          }}
        />
      )}

      {showShareModal && (
        <ShareModal
          bookId={bookId}
          bookData={bookData}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};